'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { Card } from 'my-purchases/components/Card/Card'
import { DetailsButton } from 'my-purchases/components/Card/components/DetailsButton/DetailsButton'
import { Status } from 'my-purchases/components/Card/components/Status/Status'
import { Pricing } from 'my-purchases/components/Pricing/Pricing'
import type { ORDER_TYPES } from 'my-purchases/constants/orderTypes'
import type { CardStatus } from 'my-purchases/types/purchaseList'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'

import { OrderInfo } from './content/OrderInfo/OrderInfo'

import styles from './OrderCard.module.scss'

interface OrderCardProps {
	orderIds: string[]
	uniqueId?: string
	image?: string | null
	priceAmount?: {
		price: string | null
		additionalPrices?: string[] | null
	}
	type?: ORDER_TYPES
	date?: string
	status?: CardStatus[] | CardStatus | null
	orderLineCount: number
	imageAlt?: string
	noImageText?: string
	buttonLabel?: string
	isDisabled?: boolean
	articleImageId?: string
	colorId?: string
	onClick?: () => void
	checkboxConfig?: {
		isSelected?: boolean
		onSelect: (uniqueId: string) => void
	}
}
export function OrderCard({
	orderIds,
	uniqueId = orderIds[0],
	image,
	priceAmount,
	type,
	date,
	status,
	orderLineCount,
	imageAlt,
	noImageText,
	buttonLabel,
	isDisabled,
	articleImageId,
	colorId,
	onClick,
	checkboxConfig,
}: OrderCardProps) {
	const { isSmall } = useResponsive()
	const { t } = useLabels()

	const { price, additionalPrices } = priceAmount ?? {}

	return (
		<Card
			className={styles.card}
			imageSrc={image ?? ''}
			noImageText={noImageText}
			imageAlt={imageAlt}
			{...(isSmall && {
				onCardClick: onClick,
			})}
			isDisabled={isDisabled}
			articleImageId={articleImageId}
			colorId={colorId}
			checkboxConfig={checkboxConfig}
			uniqueId={uniqueId}
		>
			<div className={styles.container}>
				<div className={styles.header}>
					<OrderInfo
						orderIds={orderIds}
						date={date}
						type={type}
						orderLineCount={orderLineCount}
					/>

					{price && (
						<Pricing
							screenReaderLabel={replacePlaceholders(
								t('accessibility.myPurchases.price.text'),
								{
									1: price,
								}
							)}
							price={price}
							additionalPrices={additionalPrices}
							className={styles.price}
						/>
					)}
				</div>

				<div className={styles.separator}></div>
				<div className={styles.footer}>
					<div className={styles.status}>
						{status && <Status status={status}></Status>}
					</div>
					{!!onClick && buttonLabel && !isSmall && (
						<DetailsButton
							orderId={uniqueId}
							label={buttonLabel}
							{...(!isDisabled && {
								handleClick: onClick,
							})}
						/>
					)}
				</div>
			</div>
		</Card>
	)
}
